import { ApolloLink, Observable, Operation } from "@apollo/client"
import { z } from "zod"

// A probe that never settles must not hold GraphQL forever. Dead sockets have
// been observed to fail after 12–21s, so this cap sits past that.
export const PROBE_MAX_WAIT_MS = 30_000

type OriginProbe = { url: string; init: RequestInit }

const probesByOrigin = new Map<string, OriginProbe>()
const inFlightProbes = new Map<string, Promise<unknown>>()

const getOrigin = (url: string) => new URL(url, window.location.href).origin

/** Start one probe for the origin, or return the probe already in flight. */
function probeOrigin(origin: string) {
  const existing = inFlightProbes.get(origin)
  if (existing) return existing

  const { url, init } = probesByOrigin.get(origin)!
  let timeoutId: ReturnType<typeof setTimeout>
  const probe = Promise.race([
    fetch(url, init),
    new Promise((resolve) => {
      timeoutId = setTimeout(resolve, PROBE_MAX_WAIT_MS)
    })
  ])
    .catch(() => {})
    .finally(() => {
      clearTimeout(timeoutId)
      inFlightProbes.delete(origin)
    })
  inFlightProbes.set(origin, probe)
  return probe
}

function handleVisibilityChange() {
  if (document.visibilityState !== "visible") return
  probesByOrigin.forEach((_, origin) => {
    void probeOrigin(origin)
  })
}

/** Register the request that probes an origin when the tab becomes visible again. */
export function registerOriginProbe(url: string, init: RequestInit) {
  const origin = getOrigin(url)
  probesByOrigin.set(origin, { url, init })
  document.addEventListener("visibilitychange", handleVisibilityChange)
  return origin
}

/** The in-flight visibility probe for the URL's origin, if there is one. */
export function waitForOriginProbe(url: string) {
  return inFlightProbes.get(getOrigin(url))
}

/**
 * Hold operations for the probe URL's origin until a visibility probe has
 * settled. Place immediately before the HTTP link.
 */
export function createConnectionGateLink(probeUrl: string): ApolloLink {
  const origin = registerOriginProbe(probeUrl, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query: "{ __typename }" })
  })

  return new ApolloLink((operation, forward) => {
    const pending = inFlightProbes.get(origin)
    if (!pending) return forward(operation)

    return new Observable((observer) => {
      let closed = false
      let subscription: { unsubscribe: () => void } | undefined
      void pending.then(() => {
        if (closed) return
        subscription = forward(operation).subscribe(observer)
      })
      return () => {
        closed = true
        subscription?.unsubscribe()
      }
    })
  })
}

function isMutationOperation(operation: Operation): boolean {
  return operation.query.definitions.some(
    (definition) =>
      definition.kind === "OperationDefinition" &&
      definition.operation === "mutation"
  )
}

const FETCH_FAILURE_MESSAGES = [
  "Failed to fetch",
  "NetworkError when attempting to fetch resource.",
  "Load failed"
] as const

/** A browser fetch rejection with no HTTP response (the socket failed). */
const noStatusFetchFailureSchema = z.object({
  message: z.enum(FETCH_FAILURE_MESSAGES),
  statusCode: z.undefined()
})

export const isNoStatusFetchFailure = (error: unknown) =>
  noStatusFetchFailureSchema.safeParse(error).success

const RETRIED_AFTER_FETCH_FAILURE = "retriedAfterFetchFailure"

const QUERY_MAX_ATTEMPTS = 5
const MUTATION_MAX_ATTEMPTS = 2

/**
 * Attempts policy for RetryLink. Queries retry any error up to five attempts.
 * Allowlisted mutations retry once, only on a no-status fetch failure, because
 * a lost response can still mean the first request committed.
 */
export function createGraphqlRetryAttempts(
  retryableMutations: ReadonlySet<string>
) {
  return (count: number, operation: Operation, error: unknown): boolean => {
    if (!error) return false
    if (!isMutationOperation(operation)) return count < QUERY_MAX_ATTEMPTS
    if (
      count < MUTATION_MAX_ATTEMPTS &&
      retryableMutations.has(operation.operationName) &&
      isNoStatusFetchFailure(error)
    ) {
      operation.setContext({ [RETRIED_AFTER_FETCH_FAILURE]: true })
      return true
    }
    return false
  }
}

/**
 * Calls onRecovered when an operation succeeds after a fetch-failure retry.
 * Place immediately upstream of the RetryLink.
 */
export function createRetryRecoveryLink(
  onRecovered: (operation: Operation) => void
): ApolloLink {
  return new ApolloLink((operation, forward) =>
    forward(operation).map((result) => {
      if (operation.getContext()[RETRIED_AFTER_FETCH_FAILURE]) {
        onRecovered(operation)
      }
      return result
    })
  )
}

export function resetConnectionGateForTests() {
  probesByOrigin.clear()
  inFlightProbes.clear()
}
